<template>
	<div class="search-panel" :class="{ 'is-loading': loading }">
		<div v-if="loading" class="panel-loading-skeleton" aria-hidden="true">
			<span class="panel-loading-skeleton__search"></span>
			<span class="panel-loading-skeleton__heading"></span>
			<div class="panel-loading-skeleton__categories">
				<span v-for="item in 4" :key="item"></span>
			</div>
			<span class="panel-loading-skeleton__map"></span>
			<div class="panel-loading-skeleton__result-heading">
				<span></span>
				<span></span>
			</div>
			<div v-for="item in 3" :key="`result-${item}`" class="panel-loading-skeleton__card">
				<span class="panel-loading-skeleton__cover"></span>
				<div>
					<span class="panel-loading-skeleton__line panel-loading-skeleton__line--title"></span>
					<span class="panel-loading-skeleton__line panel-loading-skeleton__line--meta"></span>
					<span class="panel-loading-skeleton__line"></span>
				</div>
			</div>
		</div>
		<div class="search-bar" :class="{ 'is-focused': searchFocused }">
			<img class="search-icon" :src="iconSearch" alt="" aria-hidden="true" />
			<input
				v-model="keyword"
				type="search"
				class="search-input"
				:placeholder="placeholder"
				:disabled="loading"
				@focus="searchFocused = true"
				@blur="searchFocused = false"
				@keydown.enter="handleSearch"
			/>
			<span class="search-divider" aria-hidden="true"></span>
			<button type="button" class="search-btn" :disabled="loading" @click="handleSearch">
				{{ searchLabel }}
			</button>
		</div>

		<div class="category-section">
			<div class="category-title">
				<img :src="iconHot" alt="" aria-hidden="true" />
				<span>{{ t(`${I18N_NS}.hotCategories`) }}</span>
			</div>
			<div class="category-grid" :class="{ 'category-grid--en': isEnLocale }">
				<div class="category-grid-track">
					<button
						v-for="item in categories"
						:key="item.id || item.label"
						type="button"
						class="category-btn"
						:class="{
							'is-active': String(item.id) === String(activeCategoryId),
							'is-nearby': String(item.id) === 'nearby',
						}"
						@click="handleCategoryClick(item)"
					>
						<img class="category-icon" :src="resolveCategoryIcon(item)" alt="" aria-hidden="true" />
						<span>{{ getCategoryLabel(item) }}</span>
						<img
							v-if="String(item.id) === 'nearby'"
							class="category-caret"
							:src="iconCaretDown"
							alt=""
							aria-hidden="true"
						/>
					</button>
				</div>
			</div>
			<div v-if="String(activeCategoryId) === 'nearby' && regionPanelOpen" class="region-panel">
				<div v-if="regionsLoading" class="region-loading">{{ t(`${I18N_NS}.regionLoading`) }}</div>
				<button
					v-for="region in regions"
					v-else
					:key="region.id"
					type="button"
					class="region-btn"
					:class="{ 'is-active': String(region.id) === String(selectedRegionCode) }"
					@click="handleRegionSelect(region)"
				>
					{{ region.name }}
				</button>
			</div>
		</div>

		<div class="panel-scroll">
			<div ref="mapPanelRef" class="map-panel">
				<iframe
					v-if="mapFrameReady"
					class="map-frame"
					:src="googleMapEmbedUrl"
					:title="selectedMapTitle"
					loading="lazy"
					allowfullscreen
					referrerpolicy="no-referrer-when-downgrade"
				></iframe>
				<div v-else class="map-placeholder" aria-hidden="true"></div>
				<div v-if="normalizedMapPins.length" class="map-pins">
					<button
						v-for="(pin, index) in normalizedMapPins"
						:key="`${pin.name}-${index}`"
						type="button"
						class="map-pin"
						:class="{ 'is-active': index === selectedPinIndex }"
						:style="{ left: pin.left, top: pin.top }"
						:aria-label="pin.name"
						@click="selectMapPin(index, pin)"
					>
						<span class="map-pin-dot" aria-hidden="true"></span>
						<span class="map-pin-label">{{ pin.name }}</span>
					</button>
				</div>
				<button
					type="button"
					class="map-fullscreen"
					:aria-label="t(`${I18N_NS}.mapFullscreenAria`)"
					@click="openMapDialog"
				>
					<img :src="iconMapFullscreen" alt="" />
				</button>
			</div>

			<div class="result-header">
				<span class="result-title">{{ listTitle }}</span>
				<span class="result-count">{{ resultCountText }}</span>
			</div>

			<div v-if="loading" class="result-skeleton-list" aria-hidden="true">
				<div v-for="item in 3" :key="item" class="result-skeleton-card">
					<span class="result-skeleton-cover"></span>
					<div class="result-skeleton-info">
						<span class="result-skeleton-line result-skeleton-line--title"></span>
						<span class="result-skeleton-line result-skeleton-line--meta"></span>
						<span class="result-skeleton-line result-skeleton-line--address"></span>
					</div>
				</div>
			</div>

			<div
				v-for="(item, index) in institutions"
				v-else
				:key="item.id || `${item.name}-${item.cover}`"
				class="result-card"
				:class="{ 'is-selected': item.id === selectedMerchantId }"
				role="button"
				tabindex="0"
				:ref="el => setResultCardRef(el, item.id)"
				@click="selectInstitution(item, index)"
				@keydown.enter="selectInstitution(item, index)"
			>
				<img
					class="result-cover"
					:src="item.cover"
					:alt="item.name"
					loading="lazy"
					decoding="async"
				/>
				<div class="result-info">
					<div class="result-name-row">
						<span class="result-name">{{ item.name }}</span>
						<span class="result-type">{{ item.type }}</span>
					</div>
					<div class="result-rating-row">
						<span class="rating-badge">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="13"
								height="12"
								viewBox="0 0 13 12"
								fill="none"
								aria-hidden="true"
							>
								<path
									d="M5.69926 0.250434C5.87923 -0.0834787 6.37077 -0.0834776 6.55074 0.250435L8.13662 3.19284C8.20624 3.32201 8.33387 3.41218 8.48171 3.43665L11.8495 3.99407C12.2317 4.05733 12.3836 4.51192 12.1126 4.78155L9.72496 7.15748C9.62014 7.26178 9.57139 7.40767 9.59315 7.55196L10.0887 10.8389C10.1449 11.2119 9.74725 11.4928 9.39982 11.3256L6.33827 9.85156C6.20388 9.78686 6.04612 9.78686 5.91172 9.85156L2.85018 11.3256C2.50275 11.4928 2.10508 11.2119 2.16132 10.8389L2.65686 7.55196C2.67861 7.40767 2.62986 7.26178 2.52504 7.15748L0.137369 4.78155C-0.133591 4.51192 0.0183045 4.05733 0.400493 3.99407L3.76829 3.43665C3.91614 3.41218 4.04376 3.32201 4.11338 3.19284L5.69926 0.250434Z"
									fill="#F2AF19"
								/>
							</svg>
							{{ item.rating }}
						</span>
						<span class="review-count">
							{{ t(`${I18N_NS}.reviewCount`, { count: item.reviews }) }}
						</span>
					</div>
					<div class="result-address-row">
						<div class="address-text">
							<img :src="iconLocation" alt="" aria-hidden="true" />
							<span>{{ item.address }}</span>
						</div>
						<span class="result-distance">{{ item.distance }}</span>
					</div>
					<p v-if="item.desc" class="result-desc">{{ item.desc }}</p>
					<p v-if="item.favorites" class="result-favorites">{{ item.favorites }}</p>
				</div>
			</div>
		</div>

		<Teleport to="body">
			<div v-if="mapDialogVisible" class="map-dialog-mask" @click.self="closeMapDialog">
				<div class="map-dialog">
					<iframe
						class="map-dialog-frame"
						:src="googleMapEmbedUrl"
						:title="selectedMapTitle"
						loading="lazy"
						allowfullscreen
						referrerpolicy="no-referrer-when-downgrade"
					></iframe>
					<div class="map-dialog-controls map-dialog-controls--top">
						<button
							type="button"
							class="map-dialog-icon"
							:aria-label="t(`${I18N_NS}.closeMapAria`)"
							@click="closeMapDialog"
						>
							×
						</button>
					</div>
					<div class="map-dialog-controls map-dialog-controls--bottom">
						<button type="button" class="map-dialog-icon" :aria-label="t(`${I18N_NS}.zoomInAria`)">
							<img :src="iconZoomIn" alt="" />
						</button>
						<button type="button" class="map-dialog-icon" :aria-label="t(`${I18N_NS}.zoomOutAria`)">
							<img :src="iconZoomOut" alt="" />
						</button>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "~/composables/useI18n";
import {
	getInstitutionParams,
	useServiceInstitutionTracking,
} from "../../composables/useServiceInstitutionTracking";
import iconSearch from "../../image/Icon.svg";
import iconHot from "../../image/hot.svg";
import iconLocation from "../../image/location.svg";
import iconMapFullscreen from "../../image/icon-map-fullscreen.svg";
import iconZoomIn from "../../image/icon-zoom-in.svg";
import iconZoomOut from "../../image/icon-zoom-out.svg";
import iconCaretDown from "../../../m/images/icon-caret-down-figma.svg";
import catAll from "../../image/category/all.svg";
import catNearby from "../../image/category/nearby.svg";
import catTaoist from "../../image/category/taoist.svg";
import catTemple from "../../image/category/temple.svg";
import catChurch from "../../image/category/church.svg";
import catMeditation from "../../image/category/meditation.svg";
import catHealing from "../../image/category/healing.svg";
import catSpiritual from "../../image/category/spiritual.svg";
import catCounseling from "../../image/category/counseling.svg";

const I18N_NS = "serviceInstitution.pc.demo.search";
const { t, locale } = useI18n();
const isEnLocale = computed(() => locale.value === "en");
const { track } = useServiceInstitutionTracking("pc");
const googleMapLanguage = computed(() => (locale.value === "en" ? "en" : "zh-TW"));

const emit = defineEmits(["category-change", "select-institution", "search", "region-select"]);

const FALLBACK_PIN_LAYOUTS = [
	{ left: "48%", top: "42%" },
	{ left: "36%", top: "55%" },
	{ left: "58%", top: "30%" },
	{ left: "44%", top: "64%" },
	{ left: "64%", top: "48%" },
];

const CATEGORY_ICON_MAP = {
	all: catAll,
	nearby: catNearby,
	1: catTaoist,
	2: catTemple,
	3: catChurch,
	4: catMeditation,
	5: catHealing,
	6: catSpiritual,
	7: catCounseling,
};

function resolveCategoryIcon(item) {
	const id = item.id != null ? String(item.id) : "";
	return CATEGORY_ICON_MAP[id] || CATEGORY_ICON_MAP[item.id] || item.icon;
}

const props = defineProps({
	placeholder: {
		type: String,
		default: "",
	},
	searchLabel: {
		type: String,
		default: "",
	},
	categories: {
		type: Array,
		default: () => [],
	},
	activeCategoryId: {
		type: [String, Number],
		default: "all",
	},
	selectedRegionCode: {
		type: [String, Number],
		default: "",
	},
	regions: {
		type: Array,
		default: () => [],
	},
	regionsLoading: {
		type: Boolean,
		default: false,
	},
	selectedMerchantId: {
		type: [String, Number],
		default: null,
	},
	loading: {
		type: Boolean,
		default: false,
	},
	mapPins: {
		type: Array,
		default: () => [],
	},
	resultCountText: {
		type: String,
		default: "",
	},
	institutions: {
		type: Array,
		default: () => [],
	},
	listTitle: {
		type: String,
		default: "",
	},
});

const selectedPinIndex = ref(0);
const keyword = ref("");
const searchFocused = ref(false);
const mapDialogVisible = ref(false);
const mapFrameReady = ref(false);
const regionPanelOpen = ref(false);
const mapPanelRef = ref(null);
const resultCardRefs = new Map();
const impressedResultIds = new Set();
let mapObserver;
let resultObserver;
const impressionTimers = new Map();

function handleSearch() {
	emit("search", keyword.value.trim());
}

function getCategoryLabel(item) {
	if (String(item.id) === "nearby" && props.selectedRegionCode) {
		const selectedRegion = props.regions.find(
			region => String(region.id) === String(props.selectedRegionCode)
		);
		return selectedRegion?.name || item.label;
	}
	return item.label;
}

function handleCategoryClick(item) {
	if (String(item.id) === "nearby") {
		if (String(props.activeCategoryId) === "nearby") {
			regionPanelOpen.value = !regionPanelOpen.value;
			return;
		}
		regionPanelOpen.value = true;
	}
	emit("category-change", item.id);
}

function handleRegionSelect(region) {
	emit("region-select", region);
	regionPanelOpen.value = false;
}

const mapPinSource = computed(() =>
	props.mapPins?.length ? props.mapPins : props.institutions || []
);

const normalizedMapPins = computed(() =>
	mapPinSource.value
		.map((item, index) => {
			const fallbackLayout = FALLBACK_PIN_LAYOUTS[index % FALLBACK_PIN_LAYOUTS.length];
			const name = item.name || item.label || item.address || item.query || "";
			const query = buildMapQuery(item, name);

			return {
				...item,
				name,
				query,
				left: item.left || fallbackLayout.left,
				top: item.top || fallbackLayout.top,
			};
		})
		.filter(item => item.name && item.query)
);

const selectedMapPin = computed(() => normalizedMapPins.value[selectedPinIndex.value]);

const selectedMapQuery = computed(() => {
	const selectedQuery = selectedMapPin.value?.query;
	const firstInstitutionAddress = props.institutions?.find(item => item.address)?.address;

	return selectedQuery || firstInstitutionAddress || "Hong Kong";
});

const selectedMapTitle = computed(() => selectedMapPin.value?.name || selectedMapQuery.value);

const googleMapEmbedUrl = computed(
	() =>
		`https://www.google.com/maps?q=${encodeURIComponent(selectedMapQuery.value)}&output=embed&hl=${googleMapLanguage.value}`
);

watch(
	() => props.activeCategoryId,
	categoryId => {
		if (String(categoryId) !== "nearby") {
			regionPanelOpen.value = false;
		}
	}
);

watch(
	() => props.institutions.map(item => item.id).join("|"),
	() => {
		resultCardRefs.clear();
		impressedResultIds.clear();
		nextTick(refreshResultObserver);
	}
);

watch(
	() => props.selectedMerchantId,
	merchantId => {
		const nextIndex = normalizedMapPins.value.findIndex(
			item => String(item.id) === String(merchantId)
		);

		if (nextIndex >= 0) {
			selectedPinIndex.value = nextIndex;
		}
	}
);

watch(
	() => normalizedMapPins.value.length,
	length => {
		if (selectedPinIndex.value >= length) {
			selectedPinIndex.value = 0;
		}
	}
);

function buildMapQuery(item, name) {
	if (item.lat != null && item.lng != null) return `${item.lat},${item.lng}`;
	if (item.latitude != null && item.longitude != null) return `${item.latitude},${item.longitude}`;
	if (item.query) return item.query;
	if (item.address) return item.address;

	return [name, item.region].filter(Boolean).join(" ");
}

function selectMapPin(index, pin) {
	mapFrameReady.value = true;
	selectedPinIndex.value = index;
	track("service_map_interaction", {
		interaction_type: "marker_click",
		marker_id: pin.id,
		region_name: pin.name,
		map_zoom_level: "",
	});

	if (pin.id) {
		emit("select-institution", pin.id, "map_marker");
	}
}

function openMapDialog() {
	mapFrameReady.value = true;
	mapDialogVisible.value = true;
	track("service_map_control_click", {
		control_type: "fullscreen",
		map_zoom_level: "",
	});
}

function closeMapDialog() {
	mapDialogVisible.value = false;
}

function selectInstitution(item, index) {
	emit("select-institution", item.id, "result_card");
}

function setResultCardRef(el, id) {
	if (!id) return;
	if (el) {
		resultCardRefs.set(String(id), el);
	} else {
		resultCardRefs.delete(String(id));
	}
}

function refreshResultObserver() {
	if (!import.meta.client) return;
	resultObserver?.disconnect();
	resultObserver = new IntersectionObserver(
		entries => {
			entries.forEach(entry => {
				const id = entry.target.dataset.institutionId;
				if (!id || impressedResultIds.has(id)) return;

				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					if (impressionTimers.has(id)) return;
					const timer = window.setTimeout(() => {
						const institution = props.institutions.find(item => String(item.id) === id);
						if (!institution || impressedResultIds.has(id)) return;
						impressedResultIds.add(id);
						track(
							"institution_result_impression",
							getInstitutionParams(institution, {
								position: props.institutions.findIndex(item => String(item.id) === id) + 1,
							})
						);
					}, 1000);
					impressionTimers.set(id, timer);
				} else if (impressionTimers.has(id)) {
					window.clearTimeout(impressionTimers.get(id));
					impressionTimers.delete(id);
				}
			});
		},
		{ threshold: [0.5] }
	);

	props.institutions.forEach(item => {
		const el = resultCardRefs.get(String(item.id));
		if (!el) return;
		el.dataset.institutionId = String(item.id);
		resultObserver.observe(el);
	});
}

onMounted(() => {
	if (!import.meta.client) return;
	if (!("IntersectionObserver" in window)) {
		mapFrameReady.value = true;
		nextTick(refreshResultObserver);
		return;
	}
	mapObserver = new IntersectionObserver(
		entries => {
			if (!entries.some(entry => entry.isIntersecting)) return;
			mapFrameReady.value = true;
			track("service_map_view", {
				map_provider: "google",
				location_permission_status: "unknown",
				default_region: selectedMapQuery.value,
			});
			mapObserver?.disconnect();
		},
		{ threshold: 0.5 }
	);
	if (mapPanelRef.value) mapObserver.observe(mapPanelRef.value);
	nextTick(refreshResultObserver);
});

onBeforeUnmount(() => {
	mapObserver?.disconnect();
	resultObserver?.disconnect();
	impressionTimers.forEach(timer => window.clearTimeout(timer));
	impressionTimers.clear();
});
</script>

<style lang="scss" scoped>
.search-panel {
	position: relative;
	display: flex;
	flex: 1;
	flex-direction: column;
	min-height: 0;
	padding: 24px;
	box-sizing: border-box;

	&.is-loading > :not(.panel-loading-skeleton) {
		visibility: hidden;
	}
}

.panel-loading-skeleton {
	position: absolute;
	inset: 24px;
	z-index: 2;
	display: flex;
	flex-direction: column;
	background: #fff;
}

.panel-loading-skeleton span {
	display: block;
	background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
	background-size: 300% 100%;
	animation: institution-panel-pc-shimmer 1.35s ease-in-out infinite;
}

.panel-loading-skeleton__search {
	height: 56px;
	border-radius: 53px;
}

.panel-loading-skeleton__heading {
	width: 150px;
	height: 24px;
	margin-top: 42px;
	border-radius: 6px;
}

.panel-loading-skeleton__categories {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 16px;
	margin-top: 29px;
}

.panel-loading-skeleton__categories span {
	height: 48px;
	border-radius: 10px;
}

.panel-loading-skeleton__map {
	height: 310px;
	margin-top: 42px;
	border-radius: 20px;
}

.panel-loading-skeleton__result-heading {
	display: flex;
	justify-content: space-between;
	margin-top: 42px;
}

.panel-loading-skeleton__result-heading span:first-child {
	width: 120px;
	height: 24px;
	border-radius: 6px;
}

.panel-loading-skeleton__result-heading span:last-child {
	width: 150px;
	height: 18px;
	border-radius: 5px;
}

.panel-loading-skeleton__card {
	display: grid;
	grid-template-columns: 120px minmax(0, 1fr);
	gap: 14px;
	margin-top: 24px;
}

.panel-loading-skeleton__cover {
	height: 96px;
	border-radius: 8px;
}

.panel-loading-skeleton__line {
	height: 14px;
	margin: 10px 0 16px;
	border-radius: 4px;
}

.panel-loading-skeleton__line--title {
	width: 52%;
	height: 20px;
	margin-top: 4px;
}

.panel-loading-skeleton__line--meta {
	width: 35%;
}

@keyframes institution-panel-pc-shimmer {
	to {
		background-position: -100% 0;
	}
}

.panel-scroll {
	flex: 1;
	min-height: 0;
	margin-top: 42px;
	overflow-x: hidden;
	overflow-y: auto;
	scrollbar-width: thin;
	scrollbar-color: #d9d9d9 transparent;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-thumb {
		border-radius: 4px;
		background: #d9d9d9;
	}
}

.search-bar {
	position: relative;
	display: flex;
	align-items: center;
	height: 56px;
	padding: 0 106px 0 16px;
	border-radius: 53px;
	background: #f7f7f7;
	border: 1px solid transparent;

	&.is-focused {
		border-color: #333;
		background: #fff;
	}
}

.search-icon {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	margin-right: 18px;
}

.search-input {
	flex: 1;
	min-width: 0;
	border: none;
	background: transparent;
	color: #333;
	font-size: 18px;
	line-height: 1;
	outline: none;

	&::placeholder {
		color: #99a1af;
	}

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
}

.search-divider {
	position: absolute;
	right: 106px;
	top: 50%;
	width: 1px;
	height: 20px;
	background: #ddd;
	transform: translateY(-50%);
}

.search-btn {
	position: absolute;
	right: 0;
	top: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 106px;
	height: 56px;
	padding: 0 12px;
	border: none;
	border-radius: 53px;
	background: transparent;
	color: #333;
	font-size: 18px;
	font-weight: 400;
	line-height: 1.15;
	text-align: center;
	cursor: pointer;

	&:disabled {
		cursor: not-allowed;
		opacity: 0.6;
	}
}

.category-section {
	flex-shrink: 0;
	margin-top: 42px;
}

.category-title {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 29px;

	img {
		width: 24px;
		height: 24px;
	}

	span {
		color: #333;
		font-size: 24px;
		font-weight: 700;
		line-height: 1;
	}
}

.category-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(128px, 1fr));
	gap: 16px;

	.category-grid-track {
		display: contents;
	}

	&--en {
		display: block;

		.category-grid-track {
			display: flex;
			flex-wrap: wrap;
			align-content: flex-start;
			gap: 16px;
			width: 100%;
		}

		.category-btn {
			width: auto;
			height: 48px;
			min-height: unset;
			padding: 0 16px;

			span {
				white-space: nowrap;
			}
		}
	}
}

.category-btn {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	width: 100%;
	min-height: 48px;
	padding: 8px 10px;
	border: 1px solid #efefef;
	border-radius: 10px;
	background: #fff;
	color: #999;
	font-size: 16px;
	line-height: 1.2;
	text-align: center;
	cursor: pointer;

	img {
		width: 20px;
		height: 20px;
		flex-shrink: 0;
	}

	.category-icon {
		filter: grayscale(1) brightness(0) opacity(0.38);
	}

	span {
		min-width: 0;
		white-space: normal;
	}

	&.is-active {
		border-color: #333;
		color: #333;
		font-weight: 700;

		.category-icon {
			filter: grayscale(1) brightness(0) opacity(1);
		}
	}

	&.is-nearby {
		padding-right: 8px;
	}
}

.category-caret {
	flex-shrink: 0;
	width: 16px;
	height: 16px;
}

.region-panel {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 16px;
}

.region-loading {
	width: 100%;
	color: #999;
	font-size: 14px;
	line-height: 1.4;
}

.region-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-height: 36px;
	padding: 8px 12px;
	border: 1px solid #efefef;
	border-radius: 8px;
	background: #fff;
	color: #666;
	font-size: 14px;
	line-height: 1.2;
	cursor: pointer;

	&.is-active {
		border-color: #333;
		color: #333;
		font-weight: 700;
	}
}

.map-panel {
	position: relative;
	height: 310px;
	margin-top: 0;
	overflow: hidden;
	border: 1px solid #efefef;
	border-radius: 20px;
}

.map-frame {
	display: block;
	width: 100%;
	height: 100%;
	border: 0;
}

.map-placeholder {
	width: 100%;
	height: 100%;
	background: linear-gradient(135deg, rgba(242, 175, 25, 0.08), rgba(127, 147, 229, 0.08)), #f5f5f5;
}

.map-pins {
	position: absolute;
	inset: 0;
	pointer-events: none;
}

.map-pin {
	position: absolute;
	display: flex;
	align-items: center;
	gap: 6px;
	max-width: 150px;
	padding: 5px 8px 5px 6px;
	border: none;
	border-radius: 16px;
	background: #fff;
	box-shadow:
		0 8px 5px rgba(0, 0, 0, 0.05),
		0 16px 12px rgba(0, 0, 0, 0.03),
		0 6px 15px rgba(0, 0, 0, 0.03);
	color: #333;
	font-size: 12px;
	font-weight: 600;
	line-height: 1;
	cursor: pointer;
	pointer-events: auto;
	transform: translate(-50%, -100%);
	transition:
		color 0.2s ease,
		transform 0.2s ease;

	&.is-active {
		color: #f2af19;
		transform: translate(-50%, -100%) scale(1.05);
	}
}

.map-pin-dot {
	flex-shrink: 0;
	width: 10px;
	height: 10px;
	border: 2px solid #fff;
	border-radius: 50%;
	background: #f2af19;
	box-shadow: 0 0 0 2px rgba(242, 175, 25, 0.24);
}

.map-pin-label {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.map-fullscreen {
	position: absolute;
	top: 16px;
	right: 16px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	border: none;
	border-radius: 12px;
	background: #fff;
	box-shadow:
		0 8px 5px rgba(0, 0, 0, 0.05),
		0 16px 12px rgba(0, 0, 0, 0.03),
		0 6px 15px rgba(0, 0, 0, 0.03);
	cursor: pointer;

	img {
		width: 24px;
		height: 24px;
	}
}

.result-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
	margin-top: 42px;
}

.result-title {
	color: #333;
	font-size: 24px;
	font-weight: 700;
	line-height: 1;
}

.result-count {
	color: #999;
	font-size: 18px;
	line-height: 1.25;
	text-align: right;
}

.result-card {
	display: flex;
	min-height: 156px;
	margin-top: 24px;
	cursor: pointer;
	position: relative;
	transition: transform 0.2s ease;

	&:not(.is-selected):hover {
		transform: translateY(-1px);
	}

	&:not(.is-selected):hover::after {
		content: "";
		position: absolute;
		inset: 0;
		border: 1px solid #f2af19;
		border-radius: 10px;
		box-sizing: border-box;
		pointer-events: none;
	}

	&.is-selected::after {
		content: "";
		position: absolute;
		inset: 0;
		border: 2px solid #f2af19;
		border-radius: 10px;
		box-sizing: border-box;
		pointer-events: none;
	}
}

.map-dialog-mask {
	position: fixed;
	inset: 0;
	z-index: 3000;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(0, 0, 0, 0.5);
}

.map-dialog {
	position: relative;
	width: 1314px;
	height: 768px;
	max-width: calc(100vw - 80px);
	max-height: calc(100vh - 80px);
	padding: 16px;
	box-sizing: border-box;
	border-radius: 20px;
	background: #fff;
	overflow: hidden;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
}

.map-dialog-frame {
	display: block;
	width: 100%;
	height: 100%;
	border: 0;
	border-radius: 16px;
}

.map-dialog-controls {
	position: absolute;
	right: 33px;
	display: flex;
	flex-direction: column;
	gap: 0;
	overflow: hidden;
	border-radius: 12px;
	background: #fff;
	box-shadow:
		0 8px 5px rgba(0, 0, 0, 0.05),
		0 16px 12px rgba(0, 0, 0, 0.03),
		0 6px 15px rgba(0, 0, 0, 0.03);
}

.map-dialog-controls--top {
	top: 34px;
}

.map-dialog-controls--bottom {
	bottom: 32px;
}

.map-dialog-icon {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 48px;
	height: 48px;
	padding: 0;
	border: none;
	background: #fff;
	color: #333;
	font-size: 32px;
	line-height: 1;
	cursor: pointer;

	+ .map-dialog-icon {
		border-top: 1px solid #efefef;
	}

	img {
		width: 24px;
		height: 24px;
	}
}

.result-cover {
	flex-shrink: 0;
	width: 156px;
	min-height: 156px;
	align-self: stretch;
	border-radius: 10px 0 0 10px;
	object-fit: cover;
}

.result-info {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	justify-content: center;
	gap: 8px;
	padding: 8px 16px;
	border: 1px solid #efefef;
	border-left: none;
	border-radius: 0 10px 10px 0;
}

.result-name-row {
	display: flex;
	align-items: flex-start;
	flex-wrap: wrap;
	gap: 4px;
}

.result-name {
	min-width: 0;
	color: #333;
	font-size: 18px;
	font-weight: 600;
	line-height: 1.3;
}

.result-type {
	max-width: 100%;
	padding: 4px;
	border-radius: 2px;
	background: rgba(242, 175, 25, 0.08);
	color: #f2af19;
	font-size: 14px;
	font-weight: 600;
	line-height: 1.2;
}

.result-rating-row {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}

.rating-badge {
	display: inline-flex;
	align-items: center;
	gap: 1px;
	height: 20px;
	padding: 3px 6px 3px 4px;
	border-radius: 2px;
	background: #fff9e5;
	color: #595959;
	font-size: 14px;
	font-weight: 600;
	line-height: 12px;

	svg {
		flex-shrink: 0;
		width: 13px;
		height: 12px;
	}
}

.review-count {
	color: #333;
	font-size: 14px;
	font-weight: 600;
	line-height: 1.25;
}

.result-address-row {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 12px;
}

.address-text {
	display: flex;
	align-items: center;
	gap: 2px;
	min-width: 0;

	img {
		flex-shrink: 0;
		width: 16px;
		height: 16px;
	}

	span {
		color: #333;
		font-size: 14px;
		font-weight: 600;
		line-height: 1.25;
		white-space: normal;
	}
}

.result-distance {
	flex-shrink: 0;
	color: #333;
	font-size: 14px;
	font-weight: 600;
	line-height: 14px;
	text-align: center;
}

.result-desc {
	margin: 0;
	color: #595959;
	font-size: 14px;
	font-weight: 600;
	line-height: 1.35;
}

.result-favorites {
	margin: 0;
	color: #595959;
	font-size: 14px;
	font-weight: 600;
	line-height: 1.35;
	text-align: left;
}

.result-skeleton-list {
	display: grid;
	gap: 16px;
}
.result-skeleton-card {
	display: flex;
	gap: 14px;
	padding: 14px;
	border: 1px solid #eee;
	border-radius: 12px;
}
.result-skeleton-cover,
.result-skeleton-line {
	display: block;
	background: linear-gradient(90deg, #eee 25%, #fafafa 42%, #eee 62%);
	background-size: 300% 100%;
	animation: institution-list-shimmer 1.35s ease-in-out infinite;
}
.result-skeleton-cover {
	flex: 0 0 120px;
	width: 120px;
	height: 96px;
	border-radius: 8px;
}
.result-skeleton-info {
	flex: 1;
	min-width: 0;
	padding-top: 4px;
}
.result-skeleton-line {
	height: 14px;
	margin-bottom: 15px;
	border-radius: 4px;
}
.result-skeleton-line--title {
	width: 52%;
	height: 20px;
}
.result-skeleton-line--meta {
	width: 35%;
}
.result-skeleton-line--address {
	width: 82%;
}
@keyframes institution-list-shimmer {
	to {
		background-position: -100% 0;
	}
}
</style>
